import { ReactNode } from "react";
import { useParams } from "react-router-dom";

import { ISpirometryTrial } from "../../../@types/admin/AdminSpirometry";
import { useSpirometrySession } from "../../../hooks/queries/admin/spirometry";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  AdminBreadcrumb,
  ErrorMessage,
  FilterGroup,
  FilterLabel,
  FormGrid,
  ModalJsonContainer,
  PageContainer,
  PageHeader,
  PageIntro,
  PageTitle,
  ReadOnlyValue,
  SectionCaption,
  SectionCard,
  SectionHeading,
  SectionTitle,
  StatCard,
  StatLabel,
  StatsGrid,
  StatValue,
  StatusBadge,
  TableLoadingState,
} from "../shared";
import { formatTwoDecimals, isBackfilled } from "./AdminSpirometrySessions";

/** The API's acceptability vocabulary; anything else reads as a failure. */
export function acceptabilityTone(value: string) {
  const normalised = value.trim().toLowerCase();
  if (normalised === "acceptable") return "active";
  if (normalised === "usable") return "warning";
  return "error";
}

/** `parameters` is jsonb `[{ name, value }]`; null when it isn't that shape. */
export function parseParameters(
  raw: string | null
): { name: string; value: unknown }[] | null {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) &&
      parsed.every((item) => item && typeof item.name === "string")
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function Field({
  label,
  children,
  full = false,
}: {
  label: string;
  children: ReactNode;
  full?: boolean;
}) {
  return (
    <FilterGroup span={full ? "full" : undefined} compact>
      <FilterLabel>{label}</FilterLabel>
      <ReadOnlyValue>{children ?? "-"}</ReadOnlyValue>
    </FilterGroup>
  );
}

function text(value: string | number | null) {
  return value == null || value === "" ? "-" : value;
}

function published(value: boolean | null) {
  if (value == null) return "-";
  return value ? "Yes" : "No";
}

function Acceptability({ value }: { value: string | null }) {
  return value ? (
    <StatusBadge status={acceptabilityTone(value)}>{value}</StatusBadge>
  ) : (
    <>-</>
  );
}

function Parameters({ raw }: { raw: string | null }) {
  const parameters = parseParameters(raw);
  if (parameters === null) {
    return <ModalJsonContainer>{raw}</ModalJsonContainer>;
  }
  if (parameters.length === 0) {
    return <ReadOnlyValue>-</ReadOnlyValue>;
  }
  return (
    <FormGrid>
      {parameters.map((parameter) => (
        <Field key={parameter.name} label={parameter.name.toUpperCase()}>
          {/* ArtiQ sends null FEV1/FVC for a rejected blow. */}
          {parameter.value == null ? "-" : String(parameter.value)}
        </Field>
      ))}
    </FormGrid>
  );
}

function TrialCard({ trial }: { trial: ISpirometryTrial }) {
  return (
    <SectionCard>
      <SectionHeading>
        <SectionTitle>Blow {text(trial.trialId)}</SectionTitle>
        <SectionCaption>
          Result {trial.id} · Test {text(trial.spirometryTestId)} · Measurement{" "}
          {text(trial.patientMeasurementId)} ·{" "}
          {getLongFormattedDate(trial.testStartTime, "-")}
        </SectionCaption>
      </SectionHeading>
      <FormGrid>
        <Field label="FEV1 (L)">{formatTwoDecimals(trial.measuredFev1)}</Field>
        <Field label="FVC (L)">{formatTwoDecimals(trial.measuredFvc)}</Field>
        <Field label="PEF (L/s)">{formatTwoDecimals(trial.measuredPef)}</Field>
        <Field label="FEV1/FVC">
          {formatTwoDecimals(trial.measuredFev1Fvc)}
        </Field>
        <Field label="FEV1 acceptability">
          <Acceptability value={trial.fev1Acceptability} />
        </Field>
        <Field label="FVC acceptability">
          <Acceptability value={trial.fvcAcceptability} />
        </Field>
        <Field label="Result label (pMp)">{text(trial.resultLabelPmp)}</Field>
        <Field label="Published">{published(trial.publish)}</Field>
        <Field label="FEV1 acceptability reason">
          {text(trial.fev1AcceptabilityReason)}
        </Field>
        <Field label="FVC acceptability reason">
          {text(trial.fvcAcceptabilityReason)}
        </Field>
        <Field label="Acceptability warnings">
          {text(trial.acceptabilityWarnings)}
        </Field>
        <Field label="Patient header (pMp)">
          {text(trial.patientHeaderPmp)}
        </Field>
        <Field label="Reason message (pMp)" full>
          {text(trial.reasonMessagePmp)}
        </Field>
        <FilterGroup span="full">
          <FilterLabel>ArtiQ parameters</FilterLabel>
          <Parameters raw={trial.parameters} />
        </FilterGroup>
      </FormGrid>
    </SectionCard>
  );
}

/**
 * One spirometry session and its blows (EN-4125), replacing the CMS session
 * form and its inline results table. Read-only; shares the list page's
 * `spirometry:access`.
 */
export function AdminSpirometrySessionDetails() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const parsedId = Number(sessionId);
  const isValidId = Number.isInteger(parsedId) && parsedId > 0;

  const { session, isLoading, isError, isNotFound } = useSpirometrySession(
    isValidId ? parsedId : undefined
  );

  const currentLabel = isValidId ? `Session ${parsedId}` : "Session details";
  document.title = `${currentLabel} - Admin`;

  const renderBody = () => {
    if (!isValidId) {
      return <ErrorMessage>That session id isn&apos;t valid.</ErrorMessage>;
    }
    if (isError) {
      return (
        <ErrorMessage>
          {isNotFound
            ? "That spirometry session doesn't exist."
            : "Failed to load the spirometry session. Please try again."}
        </ErrorMessage>
      );
    }
    if (isLoading || !session) {
      return <TableLoadingState />;
    }

    // Backfilled sessions store 0 for the best values: show them as absent.
    const best = (value: number | null) =>
      isBackfilled(session) ? null : value;

    return (
      <>
        <StatsGrid>
          <StatCard>
            <StatLabel>Overall grade</StatLabel>
            <StatValue>{text(session.overallGrade)}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>FEV1 / FVC grade</StatLabel>
            <StatValue>
              {session.fev1Grade || "-"} / {session.fvcGrade || "-"}
            </StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Best FEV1 (L)</StatLabel>
            <StatValue>
              {formatTwoDecimals(best(session.bestTrialFev1))}
            </StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Best FVC (L)</StatLabel>
            <StatValue>
              {formatTwoDecimals(best(session.bestTrialFvc))}
            </StatValue>
          </StatCard>
        </StatsGrid>

        <SectionCard>
          <SectionHeading>
            <SectionTitle>Session</SectionTitle>
            <SectionCaption>{session.sessionId}</SectionCaption>
          </SectionHeading>
          <FormGrid>
            <Field label="Patient">
              {session.patientId == null
                ? "-"
                : `${session.patientName || "Unnamed"} (${session.patientId})`}
            </Field>
            <Field label="Analysed on">
              {isBackfilled(session)
                ? "Not analysed"
                : getLongFormattedDate(session.analysedOn, "-")}
            </Field>
            <Field label="API version">{text(session.version)}</Field>
            <Field label="Published">{published(session.publish)}</Field>
            <Field label="FEV1 score">{text(session.fev1Score)}</Field>
            <Field label="FVC score">{text(session.fvcScore)}</Field>
            <Field label="Best PEF">{text(best(session.bestTrialPef))}</Field>
            <Field label="Best TFE">{text(best(session.bestTrialTfe))}</Field>
            <Field label="Best trial index">
              {text(best(session.bestTrialIndex))}
            </Field>
            {/* Only Django-era sessions carry these; hidden rather than a dash on every new one. */}
            {session.bestTrialFev1Index != null && (
              <Field label="Best FEV1 index">
                {session.bestTrialFev1Index}
              </Field>
            )}
            {session.bestTrialFvcIndex != null && (
              <Field label="Best FVC index">{session.bestTrialFvcIndex}</Field>
            )}
          </FormGrid>
        </SectionCard>

        {session.trials.length === 0 ? (
          <SectionCard>
            <ReadOnlyValue>This session has no blows.</ReadOnlyValue>
          </SectionCard>
        ) : (
          session.trials.map((trial) => (
            <TrialCard key={trial.id} trial={trial} />
          ))
        )}
      </>
    );
  };

  return (
    <PageContainer>
      <PageIntro>
        <AdminBreadcrumb
          items={[
            { label: "Spirometry sessions", to: "/admin/patients/spirometry" },
            { label: currentLabel },
          ]}
        />
        <PageHeader>
          <PageTitle>{currentLabel}</PageTitle>
        </PageHeader>
      </PageIntro>

      {renderBody()}
    </PageContainer>
  );
}
